<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <StatePanel
    :is-expanded
    :overlaid
    :full-height="showDetail && !openedFromMarker(activeDetail)"
    :scroll-body="false"
    collapsed-radius="46px"
    expanded-radius="1.5rem"
    @collapse="navigateToHome"
  >
    <template #header>
      <div
        class="rounded-full grid grid-cols-3 md:border-2 md:border-[#CECECE]/50 md:bg-[#CECECE]/50"
      >
        <RevButton
          v-for="item in IMPACT_NAVIGATION"
          :key="item.route"
          :class="[
            'rounded-full py-4 md:max-lg:py-8 !border-none [&_span.truncate]:!whitespace-normal',
            {
              'bg-[white]/40 md:bg-[white] hover:bg-[white]/40 md:hover:bg-[white]':
                activeImpact === item.route,
              'hover:!bg-action-default-min': activeImpact !== item.route,
            },
          ]"
          variant="secondary"
          size="small"
          @click="handleNavigate(item.route)"
        >
          <span class="flex items-center gap-x-4 max-md:flex-col">
            <!-- Icon stacked over the label on phone; inline beside it
                             from tablet up (where the label shrinks to fit one line). -->
            <RevIllustration
              :src="item.icon"
              alt=""
              :width="30"
              :height="30"
              class="size-[30px] md:max-lg:h-22 md:max-lg:w-22"
              aria-hidden="true"
            />
            <span
              class="raw-body-3 !leading-none max-lg:whitespace-normal md:max-lg:text-start md:max-lg:!text-[11px] breakWord"
            >
              {{ i18n(item.label) }}
            </span>
          </span>
        </RevButton>
      </div>
    </template>

    <template #body>
      <ContentSwap>
        <ArticleDetail v-if="showDetail" :key="`detail-${activeDetail}`" />
        <div
          v-else-if="activeImpactType !== null"
          :key="`list-${activeImpactType}`"
          class="flex min-h-0 flex-1 flex-col"
        >
          <LenisScroll viewport-class="flex-1">
            <div class="grid gap-16">
              <ImpactIntro :impact="activeImpactType" />

              <hr
                class="h-[1px] border-0 bg-[black]/15 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
              />

              <ArticleList :articles="filteredArticles" />
            </div>
          </LenisScroll>
        </div>
      </ContentSwap>
    </template>
  </StatePanel>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'

import StatePanel from '~/scopes/campaign/components/StatePanel.vue'
import ContentSwap from '~/scopes/campaign/components/UI/ContentSwap.vue'
import LenisScroll from '~/scopes/campaign/components/UI/LenisScroll.vue'
import ArticleDetail from '~/scopes/campaign/components/impacts/ArticleDetail.vue'
import ArticleList from '~/scopes/campaign/components/impacts/ArticleList.vue'
import ImpactIntro from '~/scopes/campaign/components/impacts/ImpactIntro.vue'
import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { useMapMarkerOrigin } from '~/scopes/campaign/composables/useMapMarkerOrigin'
import {
  type ModalState,
  useModalState,
} from '~/scopes/campaign/composables/useModalState'
import {
  MODAL_ROUTES,
  type ModalRoute,
  isImpactRoute,
  routeImpact,
} from '~/scopes/campaign/const/config.const'
import { IMPACT_NAVIGATION } from '~/scopes/campaign/const/impact-navigation.const'
import {
  type ImpactType,
  articlesForImpact,
} from '~/scopes/campaign/data/articles/data'

const i18n = useI18n()
const { trackClick } = useTracking()
const {
  activeModal,
  activeDetail,
  activeArticle,
  quickMetal,
  lastImpact,
  navigate,
  navigateToHome,
} = useModalState()

// Rail mode = tablet or desktop (≥768): a persistent left rail, always expanded,
// no collapse. Phone (<768) is a bottom sheet that collapses to a pill. SSR-guarded:
// the server and first client render both compute `false`, then it flips after mount
// — so we never trigger a hydration mismatch. (See TABLET_MIN_WIDTH.)
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})
const isPhone = useIsPhone()
const isRail = computed(() => mounted.value && !isPhone.value)

// Rail (tablet/desktop): always expanded. Phone: route-driven — collapses to a pill
// when the URL leaves impacts. But a route modal (metals/video/…) covering an impact
// keeps it expanded (hidden) behind the modal, so closing back to the impact doesn't
// replay a pill→sheet grow under the swap-in. `?metal=` quick views keep an impact
// route active, so the isImpactRoute clause already covers them.
const isExpanded = computed(
  () =>
    isRail.value ||
    isImpactRoute(activeModal.value) ||
    (activeModal.value !== null && lastImpact.value !== null),
)

// A ResponsiveModal (metals/video/take-action/sources or the `?metal=` quick view)
// is open. Below lg there is room for one column only, so this one swaps out (see
// StatePanel `overlaid`) and the map takes the block it frees.
const overlaid = computed(
  () =>
    quickMetal.value !== null ||
    (activeModal.value !== null && !isImpactRoute(activeModal.value)),
)

// The impact the panel is showing. Desktop always resolves to one (the
// remembered impact, or the first impact by default); phone is null unless an
// impact route is active.
const activeImpact = computed<ModalRoute | null>(() => {
  if (isImpactRoute(activeModal.value)) return activeModal.value
  if (isRail.value) return lastImpact.value ?? MODAL_ROUTES.ENVIRONMENTAL_IMPACT
  return null
})

// `activeModal` reports an impact over an article too, so it can't discriminate here.
const showDetail = computed(() => activeArticle.value !== null)

// An article opens full height, except when a map marker opened it: growing over
// the map the user just tapped is no way to answer a tap.
const { openedFromMarker, clearMarkerOrigin } = useMapMarkerOrigin()
watch(showDetail, (open) => {
  if (!open) clearMarkerOrigin()
})

// Single source of truth for the active impact type ('environmental' | ...):
// keys the intro header, filters the article list, and gates the body. Always
// resolves for an impact route (or null).
const activeImpactType = computed<ImpactType | null>(() =>
  routeImpact(activeImpact.value),
)

const filteredArticles = computed(() => {
  const type = activeImpactType.value
  return type ? articlesForImpact(type) : []
})

function handleNavigate(state: ModalState) {
  trackClick({ zone: 'modal-impacts', name: state })
  if (activeModal.value === state) {
    if (showDetail.value) {
      // From an article, step back to the expanded impact list rather
      // than closing the whole sheet.
      navigate(state)
    } else if (isPhone.value) {
      // Phone list → collapse to the pill. The rail (tablet/desktop) can't collapse.
      navigateToHome()
    }
  } else {
    navigate(state)
  }
}
</script>

<style lang="css" scoped>
.breakWord {
  word-break: break-word;
}
</style>
